import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import { LoaderCircle } from 'lucide-react'

import { cn } from '@/lib/utils/cn'

/** DS metrics (components.css .tr-btn / .tr-iconbtn); `outline` is the DS "secondary" button. */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-md border border-transparent font-medium whitespace-nowrap transition-[background-color,border-color,box-shadow,color] duration-(--dur-fast) ease-standard outline-none select-none focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-45 aria-busy:cursor-progress [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-brand text-brand-fg hover:bg-brand-hover active:bg-brand-pressed',
        outline:
          'border-border bg-panel text-fg shadow-xs hover:border-border-strong hover:bg-hover-subtle active:bg-fill-muted',
        secondary:
          'border-border bg-panel text-fg shadow-xs hover:border-border-strong hover:bg-hover-subtle active:bg-fill-muted',
        ghost: 'bg-transparent text-fg hover:bg-hover active:bg-fill-strong',
        destructive:
          'bg-danger-solid text-brand-fg hover:bg-danger-hover focus-visible:shadow-focus-danger active:bg-danger-hover',
        link: 'h-auto px-0 text-fg-link underline-offset-4 hover:underline',
      },
      size: {
        sm: "h-7 gap-1.25 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        default: 'h-8 gap-1.5 px-3 text-sm',
        lg: 'h-10 gap-2 px-4 text-base',
        'icon-xs': "size-6 rounded-sm text-fg-secondary [&_svg:not([class*='size-'])]:size-3.5",
        'icon-sm': 'size-7 text-fg-secondary',
        icon: 'size-8 text-fg-secondary',
        'icon-lg': 'size-10 text-fg-secondary',
      },
    },
    compoundVariants: [
      // Icon buttons are quiet until hovered (DS .tr-iconbtn).
      { variant: 'ghost', size: ['icon-xs', 'icon-sm', 'icon', 'icon-lg'], class: 'hover:text-fg' },
      // Links sit in running text: no control height or padding from the size.
      { variant: 'link', class: 'h-auto px-0' },
    ],
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Shows a spinner, sets aria-busy and disables the button. */
    loading?: boolean
  }

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && !asChild ? (
        <>
          <LoaderCircle className="animate-spin" aria-hidden="true" />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
export type { ButtonProps }
